import { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import {
  useFetchTicketTipos,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  useDeleteCatalogItem,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import {
  ITicketTipo,
  ITicketCategoria,
  ITicketSubcategoria,
  ITicketArea,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

import FormCatalogo from './FormCatalogo';

interface ArbolCategoriasProps {
  onRefresh?: () => void;
  /**
   * Si se pasa, filtra Tipos / Categorias / Subcategorias por esa area
   * (respetando el flag api2_area.incluir_tipos_legacy del backend). Sin
   * este prop el componente trae el catalogo completo, incluyendo legacy
   * y otras areas — caso admin global "todas las areas".
   */
  areaId?: string | null;
  /** Áreas que ofrece el selector (admin: todas; supervisor: las suyas). */
  areasEditables?: ITicketArea[];
  isSuperUser?: boolean;
}

const PK_FIELDS: Record<string, string> = {
  tipos: 'id_ticket_tipo',
  categorias: 'id_ticket_categoria',
  subcategorias: 'id_ticket_subcategoria',
};

const ArbolCategorias: React.FC<ArbolCategoriasProps> = ({
  areaId,
  areasEditables = [],
  isSuperUser = false,
}) => {
  const [selectedTipo, setSelectedTipo] = useState<ITicketTipo | null>(null);
  const [selectedCategoria, setSelectedCategoria] = useState<ITicketCategoria | null>(null);
  const [editItem, setEditItem] = useState<{
    type: 'tipos' | 'categorias' | 'subcategorias';
    data: any;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'tipos' | 'categorias' | 'subcategorias';
    item: any;
    name: string;
  } | null>(null);

  // Creacion por columna (tab unificado "Arbol de Creacion"): el boton "+"
  // de cada columna abre FormCatalogo en modo crear, precargando el padre
  // seleccionado (tipo para categorias, categoria para subcategorias).
  const [createType, setCreateType] = useState<
    'tipos' | 'categorias' | 'subcategorias' | null
  >(null);

  // Área seleccionada para el árbol. Arranca con el filtro global de la
  // página pero se puede cambiar aquí mismo: antes este tab no tenía selector
  // y el admin veía los tipos de TODAS las áreas mezclados sin poder saber
  // de qué árbol era cada uno.
  const [areaSel, setAreaSel] = useState<string>(areaId || '');
  useEffect(() => {
    setAreaSel(areaId || '');
  }, [areaId]);
  const handleChangeArea = (value: string) => {
    setAreaSel(value);
    // El tipo/categoría seleccionados pueden ser de otra área: se limpian.
    setSelectedTipo(null);
    setSelectedCategoria(null);
  };

  const { data: tiposRes, isLoading: loadingTipos } = useFetchTicketTipos({
    areaId: areaSel || undefined,
  });
  // Cuando el usuario selecciona un Tipo, se filtran las categorias por
  // ese tipo y el endpoint ignora el area_id (ya esta acotado al tipo).
  // Cuando NO hay tipo seleccionado, se pasa el areaId para que el backend
  // limite tambien las listas iniciales por area.
  const { data: categoriasRes, isLoading: loadingCategorias } = useFetchTicketCategorias(
    selectedTipo?.id_ticket_tipo,
    undefined,
    selectedTipo ? undefined : (areaSel || undefined),
  );
  const { data: subcategoriasRes, isLoading: loadingSubs } = useFetchTicketSubcategorias(
    selectedCategoria?.id_ticket_categoria,
    undefined,
    selectedCategoria ? undefined : (areaSel || undefined),
  );

  // erpApi.get() ya unwrappea axios.data, los rows viven en xxxRes.data
  const tipos = (tiposRes as any)?.data ?? [];
  const categorias = (categoriasRes as any)?.data ?? [];
  const subcategorias = (subcategoriasRes as any)?.data ?? [];

  // Mutations
  const updateTipoMutation = useUpdateCatalogItem('tipos');
  const updateCategoriaMutation = useUpdateCatalogItem('categorias');
  const updateSubcategoriaMutation = useUpdateCatalogItem('subcategorias');
  const deleteTipoMutation = useDeleteCatalogItem('tipos');
  const deleteCategoriaMutation = useDeleteCatalogItem('categorias');
  const deleteSubcategoriaMutation = useDeleteCatalogItem('subcategorias');
  const createTipoMutation = useCreateCatalogItem('tipos');
  const createCategoriaMutation = useCreateCatalogItem('categorias');
  const createSubcategoriaMutation = useCreateCatalogItem('subcategorias');

  const getMutation = (type: 'tipos' | 'categorias' | 'subcategorias') => {
    switch (type) {
      case 'tipos': return { update: updateTipoMutation, delete: deleteTipoMutation };
      case 'categorias': return { update: updateCategoriaMutation, delete: deleteCategoriaMutation };
      case 'subcategorias': return { update: updateSubcategoriaMutation, delete: deleteSubcategoriaMutation };
    }
  };

  const handleSelectTipo = (tipo: ITicketTipo) => {
    setSelectedTipo(tipo);
    setSelectedCategoria(null);
  };

  const handleSelectCategoria = (categoria: ITicketCategoria) => {
    setSelectedCategoria(categoria);
  };

  const handleEditItem = (type: 'tipos' | 'categorias' | 'subcategorias', data: any) => {
    setEditItem({ type, data });
  };

  const handleCloseEdit = () => {
    setEditItem(null);
  };

  const handleSubmitEdit = (data: any) => {
    if (!editItem) return;
    const pkField = PK_FIELDS[editItem.type];
    const id = editItem.data[pkField];
    const mutation = getMutation(editItem.type);
    mutation.update.mutate(
      { id, data },
      { onSuccess: () => setEditItem(null) }
    );
  };

  const createMutationFor = (type: 'tipos' | 'categorias' | 'subcategorias') =>
    type === 'tipos'
      ? createTipoMutation
      : type === 'categorias'
      ? createCategoriaMutation
      : createSubcategoriaMutation;

  const handleSubmitCreate = (data: any) => {
    if (!createType) return;
    createMutationFor(createType).mutate(data, {
      onSuccess: () => setCreateType(null),
    });
  };

  const handleDeleteRequest = (type: 'tipos' | 'categorias' | 'subcategorias', item: any) => {
    setDeleteConfirm({ type, item, name: item.nombre || item.codigo });
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    const pkField = PK_FIELDS[deleteConfirm.type];
    const id = deleteConfirm.item[pkField];
    const mutation = getMutation(deleteConfirm.type);
    mutation.delete.mutate(id, {
      onSuccess: () => {
        setDeleteConfirm(null);
        if (deleteConfirm.type === 'tipos') setSelectedTipo(null);
        if (deleteConfirm.type === 'categorias') setSelectedCategoria(null);
      },
      onError: () => setDeleteConfirm(null),
    });
  };

  const renderStatusChip = (active: boolean) => (
    <Chip
      label={active ? 'Activa' : 'Inactiva'}
      size="small"
      color={active ? 'success' : 'default'}
      variant="outlined"
      sx={{ ml: 1 }}
    />
  );

  const renderColumnHeader = (title: string, subtitle?: string, action?: React.ReactNode) => (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 30 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {action}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ mt: 1 }} />
    </Box>
  );

  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={24} />
    </Box>
  );

  const renderEmptyState = (message: string) => (
    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
      {message}
    </Typography>
  );

  const renderActions = (type: 'tipos' | 'categorias' | 'subcategorias', item: any) => (
    <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleEditItem(type, item)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" color="error" onClick={() => handleDeleteRequest(type, item)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const areaSelNombre = areasEditables.find((a) => a.id_area === areaSel)?.nombre;

  return (
    <>
      {/* Selector de área del árbol: sin él, el admin veía los tipos de
          todas las áreas mezclados. En "Todas" cada tipo lleva un chip con
          su área (y "Todas" para los legacy sin área). */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Área del árbol"
          value={areaSel}
          onChange={(e) => handleChangeArea(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={!isSuperUser && areasEditables.length <= 1}
          helperText={
            areaSel
              ? 'Mostrando solo el árbol de esta área (más los tipos legacy si el área los incluye)'
              : 'Todas las áreas: cada tipo lleva un chip con su área; "Todas" = legacy visible para todas'
          }
        >
          {isSuperUser && <MenuItem value="">Todas las áreas (incluye legacy)</MenuItem>}
          {areasEditables.map((a) => (
            <MenuItem key={a.id_area} value={a.id_area}>
              {a.nombre}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ height: '100%', minHeight: 400 }}>
            {renderColumnHeader(
              'Tipos',
              areaSel ? `área: ${areaSelNombre || ''}` : 'todas las áreas',
              <Tooltip title={areaSel ? `Nuevo tipo (área: ${areaSelNombre || ''})` : 'Nuevo tipo'}>
                <IconButton size="small" color="primary" onClick={() => setCreateType('tipos')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>,
            )}
            {loadingTipos ? (
              renderLoading()
            ) : tipos.length === 0 ? (
              renderEmptyState('No hay tipos registrados')
            ) : (
              <List disablePadding>
                {tipos.map((tipo: ITicketTipo) => (
                  <ListItemButton
                    key={tipo.id_ticket_tipo}
                    selected={selectedTipo?.id_ticket_tipo === tipo.id_ticket_tipo}
                    onClick={() => handleSelectTipo(tipo)}
                    sx={{ px: 2 }}
                  >
                    <ListItemText primary={tipo.nombre} />
                    {tipo.area?.nombre ? (
                      <Chip
                        label={tipo.area.nombre}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 0.5, maxWidth: 150 }}
                      />
                    ) : (
                      <Tooltip title="Tipo legacy sin área: visible para todas las áreas">
                        <Chip label="Todas" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                    {renderStatusChip(tipo.activo)}
                    {renderActions('tipos', tipo)}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ height: '100%', minHeight: 400 }}>
            {renderColumnHeader(
              'Categorias',
              selectedTipo ? `del tipo: ${selectedTipo.nombre}` : undefined,
              <Tooltip
                title={
                  selectedTipo
                    ? `Nueva categoría del tipo "${selectedTipo.nombre}"`
                    : 'Seleccione un tipo primero'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!selectedTipo}
                    onClick={() => setCreateType('categorias')}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>,
            )}
            {!selectedTipo ? (
              renderEmptyState('Seleccione un tipo')
            ) : loadingCategorias ? (
              renderLoading()
            ) : categorias.length === 0 ? (
              renderEmptyState('No hay categorias para este tipo')
            ) : (
              <List disablePadding>
                {categorias.map((cat: ITicketCategoria) => (
                  <ListItemButton
                    key={cat.id_ticket_categoria}
                    selected={
                      selectedCategoria?.id_ticket_categoria === cat.id_ticket_categoria
                    }
                    onClick={() => handleSelectCategoria(cat)}
                    sx={{ px: 2 }}
                  >
                    <ListItemText primary={cat.nombre} />
                    {cat.aplica_cierre && (
                      <Chip
                        label="Cierre"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 0.5 }}
                      />
                    )}
                    {renderStatusChip(cat.activa)}
                    {renderActions('categorias', cat)}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ height: '100%', minHeight: 400 }}>
            {renderColumnHeader(
              'Subcategorias',
              selectedCategoria ? `de la categoría: ${selectedCategoria.nombre}` : undefined,
              <Tooltip
                title={
                  selectedCategoria
                    ? `Nueva subcategoría de "${selectedCategoria.nombre}"`
                    : 'Seleccione una categoría primero'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!selectedCategoria}
                    onClick={() => setCreateType('subcategorias')}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>,
            )}
            {!selectedCategoria ? (
              renderEmptyState('Seleccione una categoria')
            ) : loadingSubs ? (
              renderLoading()
            ) : subcategorias.length === 0 ? (
              renderEmptyState('No hay subcategorias para esta categoria')
            ) : (
              <List disablePadding>
                {subcategorias.map((sub: ITicketSubcategoria) => (
                  <ListItemButton
                    key={sub.id_ticket_subcategoria}
                    sx={{ px: 2 }}
                  >
                    <ListItemText primary={sub.nombre} />
                    {renderStatusChip(sub.activa)}
                    {renderActions('subcategorias', sub)}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {createType && (
        <FormCatalogo
          open
          onClose={() => setCreateType(null)}
          catalogType={createType}
          initialData={null}
          prefill={
            createType === 'tipos'
              ? { area_id: areaSel || '' }
              : createType === 'categorias'
              ? { tipo: selectedTipo?.id_ticket_tipo || '' }
              : { categoria: selectedCategoria?.id_ticket_categoria || '' }
          }
          onSubmit={handleSubmitCreate}
          isLoading={
            createTipoMutation.isPending ||
            createCategoriaMutation.isPending ||
            createSubcategoriaMutation.isPending
          }
          tipos={tipos}
          categorias={categorias}
          areas={areasEditables}
        />
      )}

      {editItem && (
        <FormCatalogo
          open={!!editItem}
          onClose={handleCloseEdit}
          catalogType={editItem.type}
          initialData={editItem.data}
          onSubmit={handleSubmitEdit}
          isLoading={
            updateTipoMutation.isPending ||
            updateCategoriaMutation.isPending ||
            updateSubcategoriaMutation.isPending
          }
          tipos={tipos}
          categorias={categorias}
          areas={areasEditables}
        />
      )}

      {/* Confirmacion de eliminacion */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estas seguro de que deseas eliminar <strong>{deleteConfirm?.name}</strong>? Esta accion no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArbolCategorias;
